"use client"

import { Select as SelectPrimitive } from "@base-ui/react/select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "../../lib/utils"
import { useCogoPortalContainer } from "../../core/render-context"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

function SelectValue({
  className,
  ...props
}: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("cn-select-value", className)}
      {...props}
    />
  )
}

function SelectTrigger({
  className,
  children,
  style,
  ...props
}: SelectPrimitive.Trigger.Props) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size="default"
      className={cn(
        "cn-select-trigger flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm transition-colors outline-none hover:bg-accent/40 focus-visible:border-border focus-visible:ring-1 focus-visible:ring-border/60 focus-visible:ring-offset-0 aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/30 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground *:data-[slot=select-value]:flex *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      style={{
        appearance: "none",
        WebkitAppearance: "none",
        borderStyle: "solid",
        borderWidth: 1,
        ...style,
      }}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={<ChevronDown className="cn-select-trigger-icon" aria-hidden="true" />}
      />
    </SelectPrimitive.Trigger>
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: SelectPrimitive.ScrollUpArrow.Props) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn("cn-select-scroll-up-button flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronUp className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: SelectPrimitive.ScrollDownArrow.Props) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn("cn-select-scroll-down-button flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronDown className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  )
}

function SelectContent({
  className,
  children,
  style,
  ...props
}: SelectPrimitive.Popup.Props) {
  const portalContainer = useCogoPortalContainer()
  const hasAuthoredClassName =
    typeof className === "function" ||
    (typeof className === "string" && className.trim().length > 0)
  const baseClassName = cn(
    "cn-select-content relative max-h-(--available-height) min-w-(--anchor-width) overflow-hidden",
    "bg-popover text-popover-foreground border border-border rounded-md shadow-md p-1",
  )
  const popupClassName =
    typeof className === "function"
      ? (state: SelectPrimitive.Popup.State) =>
          cn(baseClassName, className(state))
      : cn(baseClassName, className)
  return (
    <SelectPrimitive.Portal container={portalContainer ?? undefined}>
      <SelectPrimitive.Positioner sideOffset={4} className="isolate z-50">
        <SelectPrimitive.Popup
          data-slot="select-content"
          // Same portaled-popover issue as PopoverContent: style-scoped
          // cn-select-content CSS doesn't reach the Portal target. Apply DS
          // surface utilities inline so dropdown menu always has bg/border/
          // shadow regardless of style scope.
          className={popupClassName}
          style={{
            minWidth: "var(--anchor-width, 8rem)",
            maxHeight: "var(--available-height, 24rem)",
            ...(!hasAuthoredClassName && {
              backgroundColor:
                "var(--color-popover, hsl(var(--popover, 0 0% 100%)))",
              color:
                "var(--color-popover-foreground, hsl(var(--popover-foreground, 30 12% 8%)))",
              borderColor:
                "var(--color-border, hsl(var(--border, 30 10% 86%)))",
            }),
            ...style,
          }}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="cn-select-group overflow-y-auto overscroll-contain">
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("cn-select-label", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "cn-select-item relative flex min-h-8 w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <span className="cn-select-item-indicator pointer-events-none absolute right-2 flex size-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText className="cn-select-item-text">
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="select-separator"
      className={cn("cn-select-separator", className)}
      {...props}
    />
  )
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
